import { ReactNode, MouseEventHandler } from 'react';
import { Calendar, Clock, User } from '../../../icons';
import { cx } from '../../../utils/cx';
import { MarkerPin01 } from '@untitledui/icons';
import {
  EventStatusBadge,
  type EventStatus
} from '../event-status-badge/event-status-badge';

export interface EventCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  title: string;
  organizationName: string;
  location?: string;
  imageSrc?: string;
  imageAlt?: string;
  category?: string;
  date?: {
    months: string;
    dates: string;
    year?: string;
  };
  time?: string;
  attendees?: {
    count: number;
    total: number;
  };
  media?: ReactNode;
  showFavoriteButton?: boolean;
  onFavoriteClick?: () => void;
  onClick?: MouseEventHandler<HTMLElement>;
  variant?: 'vertical' | 'horizontal' | 'compact';
  /** Event status to display as a pill badge */
  status?: EventStatus;
}

export function EventCard({
  title,
  organizationName,
  location,
  imageSrc,
  imageAlt,
  category,
  date,
  time,
  attendees,
  media,
  showFavoriteButton: _showFavoriteButton = true,
  onFavoriteClick: _onFavoriteClick,
  className,
  onClick,
  variant = 'vertical',
  status,
  ...props
}: EventCardProps) {
  const mediaNode =
    media ??
    (imageSrc ? (
      <img
        src={imageSrc}
        alt={imageAlt ?? title}
        className="h-full w-full object-cover"
      />
    ) : (
      <div className="h-full w-full bg-gray-100" />
    ));

  if (variant === 'compact') {
    return (
      <div
        className={cx(
          'relative flex w-full flex-col gap-1 border-b border-gray-200 bg-white p-4 first:border-t last:border-b-0',
          className
        )}
        {...props}
      >
        {onClick && (
          <button
            type="button"
            className="absolute inset-0 h-full w-full cursor-pointer border-none bg-transparent p-0"
            onClick={onClick}
            aria-label={`View details for ${title}`}
          />
        )}

        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col">
            <h3 className="m-0 text-xs font-semibold text-primary">{title}</h3>
            <p className="m-0 text-xs font-normal text-tertiary">
              {organizationName}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {category && (
              <div className="whitespace-nowrap rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-secondary">
                {category}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-3 text-xs font-normal text-tertiary">
            {date && (
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-quaternary" />
                <span>
                  {date.months} {date.dates}
                  {date.year && `, ${date.year}`}
                  {time && ` • ${time}`}
                </span>
              </div>
            )}
          </div>

          {location && (
            <div className="flex items-center gap-1.5 text-xs font-normal text-tertiary">
              <MarkerPin01 className="h-4 w-4 text-quaternary" />
              <span>{location}</span>
            </div>
          )}

          {attendees && (
            <div className="flex items-center gap-1.5 text-xs font-normal text-tertiary">
              <User className="h-4 w-4 text-quaternary" />
              <span>
                {attendees.count} / {attendees.total}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  const content = (
    <>
      <div
        className={cx(
          'relative overflow-hidden rounded-md',
          variant === 'vertical'
            ? 'aspect-square w-full'
            : 'h-full w-48 shrink-0'
        )}
      >
        {mediaNode}
        {status ? (
          <EventStatusBadge
            status={status}
            className="absolute left-2 top-2 z-10"
          />
        ) : (
          category && (
            <div className="absolute left-2 top-2 z-10 rounded-full bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-700">
              {category}
            </div>
          )
        )}
      </div>
      <div className="flex flex-col gap-1 py-2">
        <h3 className="m-0 text-sm font-semibold text-gray-900">{title}</h3>
        <p className="m-0 text-xs font-normal text-gray-500">
          {organizationName}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-3 text-xs font-normal text-gray-500">
          {date && (
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>
                {date.months} {date.dates}
                {date.year && `, ${date.year}`}
              </span>
            </div>
          )}
          {time && (
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>{time}</span>
            </div>
          )}
        </div>
        {location && (
          <div className="mt-1 flex items-center gap-1 text-xs font-normal text-gray-500">
            <MarkerPin01 className="h-4 w-4" />
            <span>{location}</span>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className={cx('relative w-full', className)} {...props}>
      {onClick ? (
        <button
          type="button"
          className={cx(
            'flex w-full cursor-pointer border-none bg-transparent p-0 text-left',
            variant === 'vertical' ? 'flex-col gap-3' : 'flex-row gap-4 h-32'
          )}
          onClick={onClick}
        >
          {content}
        </button>
      ) : (
        <div
          className={cx(
            'flex w-full',
            variant === 'vertical' ? 'flex-col gap-3' : 'flex-row gap-4 h-32'
          )}
        >
          {content}
        </div>
      )}
    </div>
  );
}
